<template>
  <div class="order-summary-container pt-7 pl-7 pr-5">
    <transition name="toast">
      <CustomizedToast
        v-show="showToast"
        @close="showToast = false"
      />
    </transition>
    <TestSheetDownloadButton v-if="showTestSheetDownloadButton"/>
    <!-- Sidebar top row with expand button and subtotal -->
    <div class="sidebar-top-row">
      <div class="sidebar-title-left">
        <button class="sidebar-expand-btn" type="button" @click="$emit('toggle-expand')">
          <span class="sidebar-expand-icon">{{ expanded ? '»' : '«' }}</span>
        </button>
        <span class="sidebar-subtotal-label">Subtotal:</span>
      </div>
      <span class="sidebar-subtotal-value">${{ formatSubtotal(currentEstimateTotal) }}</span>
    </div>
    <!-- Sidebar tabs: Products and Tubes -->
    <Tab
      v-model="activeName"
      type="underline"
      width="100%"
      group_padding="0"
      :tabs="sidebarTabs"
      :icon_size="20"
      class="sidebar-tabs-lib"
      @change="handleTabClick"
    />
    <!-- selected test & required tubes content -->
    <div v-show="activeName === 'selectedTests'" class="sidebar-items">
      <div v-show="editOrderItemList.concat(selectedItemList).concat(selectedMerchList).length > 0">
        <SelectedTestsTabPane :item-list="selectedItemList" :show-clear-buttons="expanded" :show-item-type-group="showItemTypeGroup" @handleInAppPromo="inAppPromos = $event" @handleExclusiveAddOns="exclusiveAddOns = $event" />
        <!-- <PlanAndSave v-if="paymentMethod !== 'patientPayLater'" /> -->
        <Alert
          v-if="taxableAlert"
          :height="48"
          type="alert highlight-text"
          class="flex items-center mx-3 mb-3 translate-as-whole-pointer"
        >
          <img src="@/assets/orderTest/warning_24px.svg" class="pl-3 px-2" alt="error outline">
          Patient's address required.
        </Alert>
      </div>
    </div>
    <div v-show="activeName === 'requiredTubes'" class="sidebar-items">
      <TubeTableTabPane
        :expanded="expanded"
        :updated-tube-info-count="updatedTubeInfoCount"
        :tube-info-for-require-tube-tab-order-summary="tubeInfoForRequireTubeTabOrderSummary"
      />
    </div>
    <!-- Order Total -->
    <!-- <span>{{ priceDetails }}</span> -->
    <InAppPromo v-if="inAppPromos.length > 0"class="mt-3" :in-app-promos="inAppPromos" @hideSuggestion="hideSuggestion" />
    <ExclusiveAddOns v-if="exclusiveAddOns.length > 0" class="mt-5" :add-ons="exclusiveAddOns" />
    <EstimateTotal
      v-show="paymentMethod !== 'wellProz'"
      class="mt-4 px-3 mb-5"
      :show-accessories="showAccessories"
      :lab-processing-fee="priceDetails.labProcessingFee"
      :order-total="priceDetails.orderTestsPrice"
      :contain-blood="containBlood"
      :contain-non-blood="containNonBlood"
      :delivery-method="deliveryMethod"   
      :contain-vw-test="containVwTest"
      :tax-fee="priceDetails.taxFee"
      @editOrderPriceDiffChange="editOrderPriceDiff = $event"
      @addOnOrderPriceDiffChange="addOnOrderPriceDiff = $event"
      @disableCheckout="disableCheckout"
      @estimateTotalChange="currentEstimateTotal = $event"
    />
    <template v-if="showAccessories">
    <hr v-if="selectedItemList.length > 0 && paymentMethod !== 'wellProz'" class="divide-line mt-1.5">
    <!-- Delivery Method -->
    <DeliveryMethods
      v-show="editOrderItemList.concat(selectedItemList).concat(selectedMerchList).length > 0"
      class="mt-6"
      :default-settings="defaultSettings"
      :order-draft="orderDraft"
      :kit-inventory-info="kitInventoryInfoObj"
      :tube-info-warning-alert-blood="tubeInfoWarningAlertBlood"
      :tube-info-warning-alert-non-blood="tubeInfoWarningAlertNonBlood"
    />
    <hr class="divide-line mt-5">
    </template>
    <div v-if="showAccessories"> 
      <!-- Payment method -->
      <PaymentMethods
        class="mt-5"
        :payment-method="paymentMethod"
        :default-settings="defaultSettings"
        :order-draft="orderDraft"
        :customer-state="customerState"
        :is-patient-pay-later-eligible="isPatientPayLaterEligible"
        :client-paid="orderDraft.clientPaid"
      />
      <hr class="divide-line mt-5">
      <!-- Send to Patient -->
      <SendEmailText 
        :is-send-email="sendNotification.email"
        :is-send-text="sendNotification.text"
        :email="contactInfo.email"
        :phone="contactInfo.phone"
        :default-settings="defaultSettings"
        :order-draft="orderDraft"
      />
    </div>
    <div class="mt-10 py-5">
      <Buttons 
        v-if="showAccessories"
        v-loading.fullscreen.lock="checkoutLoading" 
        element-loading-text="Processing..."
        element-loading-spinner="el-icon-loading"      
        :total-price="priceDetails.orderTestsPrice"
        :is-order-draft-not-exist="isOrderDraftNotExist"
        :draft-name-parent="draftName"
        :edit-order-price-diff="editOrderPriceDiff"
        :add-on-order-price-diff="addOnOrderPriceDiff"
        :is-disable-checkout="isDisableCheckout"
        :payment-method="paymentMethod"
        @updateDraftName="draftName = $event"
        @saveDraft="saveDraft"
        @handleCheckout="handleClickCheckout"
      />
    </div>
    <CovidWarningDialog 
      :is-show-dialog="isShowCovidWarningDialog"
      :text="covidWarningMessage" 
      @close="isShowCovidWarningDialog = false" 
    />   
    
    <ExpressTaxableDialog
      :is-show-dialog="isShowTaxablePopUp" 
      :is-selected-taxable-test="isSelectedTaxableTest"
      @close="isShowTaxablePopUp = false"
    />
    <FreeAddOnDialog
      :is-show-dialog="isShowFreeAddOnDialog"
      :options="freeAddOnOptions"
      @close="onFreeAddOnDialogClose"
    />
    <!-- <FamilyDiscountConfirmationDialog
      :is-show-dialog="isShowFamilyDiscountConfirmationDialog"
      @close="isShowFamilyDiscountConfirmationDialog = false"
      @confirm="familyDiscountDialogConfirm"
    /> -->
    <!-- New Version (Beta) Components -->
    <template v-if="isBeta">
      <DobDialog
        v-model="showDobDialog"
        :initialDob="patient?.birthdate || ''"
        @submit="onDobSubmittedFromDialog"
        @unknown-dob="onUnknownDobClick"
      />
      <DobUnder18CheckDialog
        v-model="showDobUnder18CheckDialog"
        @submit="onDobUnder18CheckSubmit"
        @back="showDobDialog = true"
      />
      <ProviderConsentDialog
        v-model="showProviderConsent"
        @submit="continueAfterConsent"
      />
      <GuardianConsentDialog
        v-model="showGuardianConsent"
        :default-guardian-email="guardianEmailFromSession || patient?.guardianEmail"
        :default-always-use-email="guardianDialogDefaultAlways || Boolean(pediatricConsentInfo?.always_use_email_consent)"
        @submit="continueAfterGuardianConsent"
      />
    </template>
    
    <!-- Old Version (Production) Components -->
    <template v-else>
      <DobDialogV1
        v-model="showDobDialog"
        :initialDob="patient?.birthdate || ''"
        @submit="onDobSubmittedFromDialogV1"
      />
      <ProviderConsentDialogV1
        v-model="showProviderConsent"
        @submit="continueAfterConsentV1"
      />
    </template>
  </div>
</template>

<script>
import SelectedTestsTabPane from "./selected-tests-tab-pane/index.vue";
// import PlanAndSave from "./plan-and-save/index.vue";
import CustomizedToast  from "./CustomizedToast.vue";
import TubeTableTabPane from "./tube-table-tab-pane/index.vue";
import EstimateTotal from "./SummaryAccessories/estimate-total/index.vue";
import PaymentMethods from "./SummaryAccessories/PaymentMethods.vue";
import DeliveryMethods from "./delivery-methods/index.vue";
import SendEmailText from "./SummaryAccessories/send-email-text/index.vue";
import Buttons from "./SummaryAccessories/Buttons.vue";
import CovidWarningDialog from "./CovidWarningDialog.vue";
import TestSheetDownloadButton from "./TestSheetDownloadButton.vue";
import ExpressTaxableDialog from '../ExpressTaxableDialog.vue'
import FreeAddOnDialog from '../FreeAddOnDialog.vue'
import InAppPromo from "./in-app-promo/index.vue";
import ExclusiveAddOns from "./exclusive-add-ons/index.vue";
import Alert from '@/views/va-orderTest/components/Alert.vue';
// import FamilyDiscountConfirmationDialog from "../../family-discount/family-discount-dialogs/FamilyDiscountConfirmationDialog.vue";
import DobDialog from './DobDialog.vue'
import ProviderConsentDialog from "./ProviderConsentDialog.vue";
import GuardianConsentDialog from "./GuardianConsentDialog.vue";
import DobUnder18CheckDialog from './DobUnder18CheckDialog.vue';
import DobDialogV1 from './DobDialogV1.vue';
import ProviderConsentDialogV1 from "./ProviderConsentDialogV1.vue";
import { mapState, mapGetters } from "vuex";
import _ from "lodash";
import { createOrderDraft, deleteDraftById } from "@/api/va-order-page/orderTest.js";
import { calculateTax } from "@/api/va-order-page/pricing/tax";
import { getLabProcessingFeeApi } from "@/api/va-order-page/chargePage.js";
import { editOrderApi } from "@/api/va-order-page/patientPage.js";
import moment from 'moment';
import { getDraftObjFroApi } from "./index.js";
import { notify } from '@/utils/notify';
import { getOrderObjForBackend } from "../../checkout-page/paymentMethod.js";
import { getSettingData, changeSetting } from '@/api/setting'
import { getRedirectUrl } from '@/utils';
import { createTubeInfo } from "@/api/va-order-page/orderTest.js";
import axios from 'axios';
import { CLOUD_SERVICE_BASE_URL } from "@/utils/const";
import { removeItem } from "@/utils/va-order-test/testAndPanelSelection";
import { updatePatientApi } from "@/api/va-order-page/orderTest.js";
import { readConsent, writeConsent, needProvider, needGuardian } from '@/utils/pediatricConsentStore';
import { getStateAbbreviation } from "@/utils/va-order-test/parsePatient.js";
import iconProductsTab from "@/assets/newOrder/vibrant-product-selected.svg";
import iconTubesTab from "@/assets/orderTest/order-page/promoPanel/icon-test-tubes.svg";
const kitInventoryApi = CLOUD_SERVICE_BASE_URL + (process.env.VUE_APP_ENV === "production"
  ? "/lis/order-inventory"
  : "/lis/order-inventory/staging"
)


export default {
  emits: ['toggle-expand'],
  components: {
    SelectedTestsTabPane,
    // PlanAndSave,
    TubeTableTabPane,
    CustomizedToast ,
    EstimateTotal,
    PaymentMethods,
    DeliveryMethods,
    SendEmailText,
    Buttons,
    CovidWarningDialog,
    ExpressTaxableDialog,
    TestSheetDownloadButton,
    FreeAddOnDialog,
    InAppPromo,
    ExclusiveAddOns,
    Alert,
    // FamilyDiscountConfirmationDialog,
    DobDialog,
    ProviderConsentDialog,
    GuardianConsentDialog,
    DobUnder18CheckDialog,
    DobDialogV1,
    ProviderConsentDialogV1,
  },
  props: {
    orderDraft: {
      type: Object,
      default: () => {},
    },
    showAccessories: {
      type: Boolean,
      default: true,
    },
    expanded: {
      type: Boolean,
      default: false,
    },
    showItemTypeGroup: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      today: new Date().toLocaleDateString(),
      activeName: "selectedTests",
      environment: process.env.VUE_APP_ENV,
      activateTube: false,
      // get patient from sessionStorage, make sure convert JSON string to object
      patient: JSON.parse(sessionStorage.getItem("patient")),
      guardianEmailFromSession: sessionStorage.getItem('guardianEmail') || '',
      draftName: "",
      showToast : false,
      showUpdateBtn: true,
      isShowCovidWarningDialog: false,
      covidWarningMessage: "",
      checkoutLoading: false,  
      editOrderPriceDiff: 0,
      addOnOrderPriceDiff: 0,
      currentEstimateTotal: 0,
      isTaxAPIError: false,
      usNames: ['United States', 'US', 'USA', 'UnitedStates', 'United States of America'],
      isSelectedTaxableTest: [],
      isShowTaxablePopUp: false,
      isShowFreeAddOnDialog: false,
      freeAddOnOptions: [],
      hasShownFreeAddOnDialog: false, 
      orderType: null,
      kitInventoryInfoObj: {},
      tubeInfoWarningAlertBlood: false,
      tubeInfoWarningAlertNonBlood: false,
      tubeInfoForRequireTubeTabOrderSummary: {},
      updatedTubeInfoCount: 0,
      isDisableCheckout: false,
      inAppPromos: [],
      exclusiveAddOns: [],
      // Family Bundle - UI hidden, keeping for potential future use
      isShowFamilyDiscountConfirmationDialog: false,
      showDobDialog: false,
      showProviderConsent: false,
      showGuardianConsent: false,
      showDobUnder18CheckDialog: false,
      pediatricConsentInfo: null,
      minorOverride: null,
      guardianDialogDefaultAlways: false,
    };
  },

  computed: {
    sidebarTabs() {
      return [
        { value: "selectedTests", label: "Products", icon: iconProductsTab },
        { value: "requiredTubes", label: "Tubes",    icon: iconTubesTab },
      ];
    },
    ...mapState(["currency"]),
    ...mapState("orderTest", [
      "defaultSettings", 
      "bundleReplaceTriggered", 
      "isPatientInfoInSessionStorage",
      "isDbsVersion",
      "isAddOnOrder",
      "addOnselectedItemList",
      "addOnPrice"
    ]),
    ...mapState("orderTest/user", ["userInfo", "updatePatientCount", "isUpdatingPatient"]),
    ...mapState("orderTest/tests", ["selectedTestIdList", "vwCategories"]),
    ...mapState("orderTest/order", [
      "labProcessingFee", 
      "sendDate", 
      "deliveryMethod",
      "paymentMethod",
      "sendNotification",
      "contactInfo",
      "plannedOrderList",
      "editOrderSampleId",
      "shippingFee",
      "taxFee",
      "conciergeFee",
      "consultationFee"
    ]),
    ...mapState("orderTest/orderDraft", ["editOrderItemList"]),
    // Family Bundle - State (UI hidden, keeping for potential future use)
    ...mapState("orderTest/familyDiscount", ["familyDiscountOrders"]),
    ...mapGetters("orderTest", [
      "selectedVwTestPrice",
      "selectedItemList",
      "orderItemListForApi",
      "selectedItemsPrice",
      "containVwTestInSelectedShortcut",
      "orderTestSupportObj"
    ]),
    ...mapGetters("orderTest/tests", ["vwTestList"]),
    ...mapGetters("orderTest/order", ["sendDateInUtc", "priceDetails", "firstOrderPriceDetails", "orderedSampleTypes"]),
    ...mapGetters("orderTest/merchandise", ["selectedMerchList"]),
    ...mapGetters("orderTest/panels", ["bundleList"]),
    ...mapGetters("orderTest/user", ["isPatientPhoneMissing"]),
    // Family Bundle - Getters (UI hidden, keeping for potential future use)
    ...mapGetters("orderTest/familyDiscount", ["familyDiscountEligible"]),
    
    isUrlFromEditOrAddOn() {
      return this.editOrderSampleId || this.isAddOnOrder
    },

    containBlood() {
      return this.editOrderItemList.concat(this.selectedItemList)
        .some(item => item.sampleTypeSets && item.sampleTypeSets.blood && item.sampleTypeSets.blood.size > 0);
    },
    containNonBlood() {
      return this.editOrderItemList.concat(this.selectedItemList)
        .some(item => item.sampleTypeSets && item.sampleTypeSets.nonBlood && item.sampleTypeSets.nonBlood.size > 0);
    },

    containDryIce() {
      return this.editOrderItemList.concat(this.selectedItemList).some(item => item.sampleTypeSets && item.sampleTypeSets.dryIce && item.sampleTypeSets.dryIce.size > 0);
    },

    isOrderDraftNotExist() {
      return _.isEmpty(this.orderDraft);
    },

    // if selected tests/bundle/custom/xCHoosey/shortcut contain any test from vw panel
    containVwTest() {
      return this.selectedVwTestPrice > 0 || this.containVwTestInSelectedShortcut;
    },    
    
    customerState() {
      if (!_.isEmpty(this.userInfo)) {
        let state = this.userInfo.customer_state ? this.userInfo.customer_state : "";
        return state ? getStateAbbreviation(state) : "";
      }
      return ""
    },
    //patient pay later is not avaible for following situations:
    // 1. has plan and save orders
    // 2. non-blood kit delivery method is supplied by office
    isPatientPayLaterEligible() {
      if (this.plannedOrderList.length > 0) {
        return false;
      }
      if (this.containNonBlood && this.deliveryMethod.nonBlood === "suppliedByClient") {
        return false;
      }
      return true;
    },
    showTestSheetDownloadButton() {
      return false;
    },
    isBeta() {
      if (_.isEmpty(this.userInfo) || !this.userInfo.beta_program_enabled || _.isEmpty(this.userInfo.beta_programs)) {
        return false;
      }
      return this.userInfo.beta_programs.includes("general_beta1");
    },
    isNewVwBeta() {
      return !!(this.userInfo?.beta_programs && this.userInfo.beta_programs.includes("new_vw"));
    },
    isPatientAddressMissing() {
      if(!this.patient) return false
      let { streetAddress, city, state, zipcode, country } = this.patient
      return !streetAddress || !city || !state || !zipcode || !country
    },
    isPatientAgeGenderMissing() {
      if(!this.patient) return false
      let { birthdate, gender } = this.patient
      return !birthdate || !gender  
    },
    taxableAlert() {
      if(!this.patient) return false
      let { selectedItemList } = this.orderTestSupportObj
      const containTaxableTest = selectedItemList.some(test => test.taxCode)
      return containTaxableTest && this.isPatientAddressMissing
    },

    skipPediatricConsentEffective() {
      const s = this.pediatricConsentInfo?.skip_pediatric_consent;
      if (typeof s === 'boolean') return s;
      return Boolean(this.defaultSettings?.skip_pediatric_consent);
    },

    alwaysEmailConsentEffective() {
      const s = this.pediatricConsentInfo?.always_use_email_consent;
      if (typeof s === 'boolean') return s;
      return Boolean(this.defaultSettings?.always_use_email_consent);
    },

    isMinorEffective() {
      if (this.minorOverride !== null) return this.minorOverride;
      if (this.patient?.birthdate) return this.isUnder18(this.patient.birthdate);
      return false;
    },
  },

  watch: {
    isPatientInfoInSessionStorage(newVal) {
      if (newVal) {
        this.patient = JSON.parse(sessionStorage.getItem("patient"));
      }
    },  
    // recalculate tax when patient info is changed
    updatePatientCount(newVal,oldVal) {
      if(newVal > oldVal) {
        this.selectedTaxableTests(this.orderItemListForApi)
      }
    },
    // disable checkout when updatePatient API is in progress
    isUpdatingPatient(newVal) {
      this.isDisableCheckout = newVal;
    },

    orderDraft() {
      this.draftName = this.orderDraft.draftName;
    },

    orderItemListForApi: _.debounce(function(newVal, oldVal) {
      // initiate tubeInfoWarningAlert
      this.tubeInfoWarningAlertBlood = false
      this.tubeInfoWarningAlertNonBlood = false
      if (!newVal) {
        return;
      }
      if (newVal.length === 0) {
        this.$store.dispatch("orderTest/order/setLabProcessingFee", 0);
        this.$store.dispatch("orderTest/order/setTaxFee", { taxFee: 0, isUseTax: false, taxId: '' });
        return
      }
      this.getLabProcessingFee(newVal);
      this.selectedTaxableTests(newVal)
      this.getTubeInfoForInternationalKitWarning(newVal)
    }, 1000),    

    bundleReplaceTriggered(newVal, oldVal) {
      if (newVal > oldVal) {
        this.showToast = true
      }
    },

    async showToast(newVal) {
      if (newVal === true) {
        await new Promise(r => setTimeout(r, 8000));
        this.showToast = false;
      }
    },

  },

  async mounted() {
    const consent = readConsent();
    this.pediatricConsentInfo = consent;
    
    if(!_.isEmpty(this.orderDraft)) {
      this.draftName = this.orderDraft.draftName;
    }

    let currentURL = window.location.href
    let currentID = currentURL.split('/').at(-1)
    if (currentID.includes('r')) {
      this.showUpdateBtn = false
    }
    
    this.isBeta&&this.getkitInventory()
  },

  methods: {
    formatSubtotal(value) {
      if (typeof value !== 'number') {
        return '0.00';
      }
      return value.toFixed(2);
    },
    handleTabClick(tabName) {
      this.activeName = tabName;
      this.clickTab({ props: { name: tabName } });
    },
    ensureSkipProvider(flag = true) {
      const next = writeConsent(flag ? { skip_pediatric_consent: true } : { skip_pediatric_consent: false });
      this.pediatricConsentInfo = next;
    },

    getGuardianEmailCandidate() {
      const fromSession = (this.guardianEmailFromSession || '').trim();
      const fromConsent = (this.pediatricConsentInfo?.guardian_email || '').trim();
      const fromPatient = (this.patient?.guardianEmail || '').trim();
      const pick = [fromSession, fromConsent, fromPatient].find(e => this.isValidEmail(e));
      console.log('getGuardianEmailCandidate啊啊啊啊啊', { fromSession, fromConsent, fromPatient, pick });
      return pick || '';
    },

    isValidEmail(email) {
      return /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(String(email || '').trim());
    },

    async getTubeInfoForInternationalKitWarning(newVal) {
      const {data} = await createTubeInfo(newVal)
      this.tubeInfoForRequireTubeTabOrderSummary = data
      this.updatedTubeInfoCount += 1
      let bloodTubeQuantity = 0
      let nonBloodTubeQuantity = 0
      let countOf4TubesAs1Tube = 0

      let usedTubeIdAndQuantityForUpdatingInventoryAPI = {}
      Object.keys(data.noOfTubes).forEach(key => {
        let convertedTubeName = this.tubeInfoNameMappingForInventory(key)
        let quantityForConvertedTubeName = data.noOfTubes[key]

        // adding tube quantity to calcualte kit quantity
        // non blood label quantity = nonBloodTubeQuantity; blood label quantity = bloodTubeQuantity / 2 ceiling
        if(convertedTubeName === -1) {
          bloodTubeQuantity += quantityForConvertedTubeName
          let bloodLabelQuantity = Math.ceil(bloodTubeQuantity / 2)
          // check blood label quantity
          if(bloodLabelQuantity > (this.kitInventoryInfoObj['12'] || 0)) {
            this.tubeInfoWarningAlertBlood = true
          }
          // skip checking blood tube quantity since it will not be considered in the warning
          return
        } else {
          nonBloodTubeQuantity += quantityForConvertedTubeName
          if(convertedTubeName !== 13 || convertedTubeName !== 12) {
            // exclude the return label count
            usedTubeIdAndQuantityForUpdatingInventoryAPI[convertedTubeName] = 0 - quantityForConvertedTubeName
            // urine4tubes and saliva4 tubes count as one tube
            if(convertedTubeName === 7 || convertedTubeName === 11) {
              countOf4TubesAs1Tube += 1
            }
          }
        }

        // tube quantity checking
        if(!this.kitInventoryInfoObj[convertedTubeName] || this.kitInventoryInfoObj[convertedTubeName] < quantityForConvertedTubeName) {
          this.tubeInfoWarningAlertNonBlood = true 
        } 
      })

      // recalculate nonBloodTubeQuantity to count 4 tubes kit type as 1 
      if(countOf4TubesAs1Tube > 0) {
        nonBloodTubeQuantity = nonBloodTubeQuantity - countOf4TubesAs1Tube + countOf4TubesAs1Tube / 4
      }

      // non blood kit return label comparing with its inventory
      if(nonBloodTubeQuantity > (this.kitInventoryInfoObj['13'] || 0)) {
        this.tubeInfoWarningAlertNonBlood = true
      }

      if(nonBloodTubeQuantity > 0) {
        usedTubeIdAndQuantityForUpdatingInventoryAPI[13] = 0 - nonBloodTubeQuantity
      }
      this.$store.dispatch("orderTest/order/setUpdateInventory", usedTubeIdAndQuantityForUpdatingInventoryAPI);
    },
    tubeInfoNameMappingForInventory(tubeNameEnum) {
      if (tubeNameEnum.includes("STOOL")) {
        return 8 
      } else if (tubeNameEnum.includes("MICROTUBE")) {
        return 5
      } else if (tubeNameEnum.includes("SALIVA_")) {
        // Saliva4
        return 11
      } else if (tubeNameEnum.includes("SALIVA") && !tubeNameEnum.includes("SALIVA_")) {
        return 10
      } else if (tubeNameEnum.includes("URINE_")) {
        // URINE4
        return 7
      } else if (tubeNameEnum.includes("URINE") && !tubeNameEnum.includes("URINE_")) {
        return 6
      } else if (tubeNameEnum.includes("FINGERPRICK")) {
        // DBS card
        return 9
      } else {
        // blood kits 
        return -1
      }
    },

    async getkitInventory() {
      try {
        let accountId = this.userInfo ? (this.userInfo.isClinic ? this.userInfo.clinic_id : this.userInfo.customer_id) : null;
        let isClinic = this.userInfo ? this.userInfo.isClinic : false;
        let accountType = isClinic ? 'clinic' : 'customer';
        let clinicId = this.userInfo ? this.userInfo.clinic_id : null;

        const token = sessionStorage.getItem("jwtToken");
        const baseUrl = `${kitInventoryApi}/kit-inventory?account_type=customer&account_id=${this.userInfo.customer_id}&clinic_id=${clinicId}`;

        const config = {  
          headers: { Authorization: "Bearer " + token }
        };
        
        const response = await axios.get(baseUrl, config);
        const data = response.data; 
        data.forEach(item => {
          this.kitInventoryInfoObj[item.item_id] = item.quantity
        })
        console.log(data,'inventory',this.kitInventoryInfoObj,'this.kitInventoryInfoObj')
      } catch (error) {
        console.log(error)
      }
    },
    selectedTaxableTests(newVal) {
      let { selectedItemList } = this.orderTestSupportObj
      this.isTaxAPIError = false
      this.isSelectedTaxableTest = []
      // Regenere id:  731
      // Regenere follow up ids: 801, 802, 803
      // NutriproZ id: 725, 726 - NY
      // NutriproZ follow up id: 800
      let selectedTaxableTestForAPI = []
      selectedItemList.forEach(test => {
        if(test.taxCode) {
          let amount = test.price.toFixed(2)
          let quantity = 1
          let reference = test.id.toString()

          // Skin care tax code: txcd_32050013
          // NutriProZ tax code: txcd_40090001
          let tax_code = test.taxCode
          let tax_behavior = "exclusive"

          selectedTaxableTestForAPI.push({amount,quantity,reference,tax_code,tax_behavior})
          this.isSelectedTaxableTest.push(test.displayName)
        }
      })
      this.selectedMerchList.forEach(item => {
        if(item.merch.taxCode) {
          let quantity = item.quantity;
          if (quantity === 0) {
            return;
          }
          let amount = (item.merch.currentPrice * quantity).toFixed(2);
          let reference = "merch_" + item.id.toString();

          let tax_code = item.merch.taxCode;
          let tax_behavior = "exclusive"

          selectedTaxableTestForAPI.push({amount,quantity,reference,tax_code,tax_behavior})
          this.isSelectedTaxableTest.push(item.merch.name);
        }
      })

      if(selectedTaxableTestForAPI.length > 0) {
        let countryName = JSON.parse(sessionStorage.getItem("patient")).country
        let customerState = JSON.parse(sessionStorage.getItem("patient")).state
        let customerZipcode = JSON.parse(sessionStorage.getItem("patient")).zipcode
        let customerCity = JSON.parse(sessionStorage.getItem("patient")).city
        let customerStreet = JSON.parse(sessionStorage.getItem("patient")).streetAddress
        let address_type = "shipping"

        let isMissingFullAddress = !countryName || !customerState || !customerZipcode || !customerCity || !customerStreet
        let customerDetailsForAPI = {countryName,customerState,customerZipcode,customerCity,customerStreet,address_type}
      
        // if default is express checkout and no address, then no need to calc tax
        if(this.defaultSettings.express_checkout&&isMissingFullAddress) {
          return
        }
        
        calculateTax({
          line_items: selectedTaxableTestForAPI,
          customer_details: customerDetailsForAPI,
          shipping_fee: this.shippingFee.total
        }).then(res => {
          this.$store.dispatch("orderTest/order/setTaxFee", { taxFee: res.data.tax_amount, isUseTax: true, taxId: res.data.id });
        }).catch(err => {
          this.isTaxAPIError = true
        })
      } else {
        this.$store.dispatch("orderTest/order/setTaxFee", { taxFee: 0, isUseTax: false, taxId: '' })
      }
    },

    // change activeTube value according to requiredTubes tab whether is active or not
    clickTab(tab) {
      if (tab.props.name === "selectedTests") {
        this.activateTube = false;
      }
      if (tab.props.name === "requiredTubes") {
        this.activateTube = true;
      }
    },

    async handleClickCheckout(orderType = null) {
      console.log(orderType,'orderType')
      this.orderType = orderType

      let eligibleAddons = []
      if (!this.isUrlFromEditOrAddOn) {
        eligibleAddons = this.collectEligibleAddons([167, 840])
        const remainingEligibleAddons = eligibleAddons.filter(id => id !== 167);
        if (remainingEligibleAddons.length > 0 && !this.hasShownFreeAddOnDialog) {
          this.freeAddOnOptions = remainingEligibleAddons.map(id => ({
            id,
            label: id === 840 ? this.$t('OrderPage[\'Cortisol Awakening Response (Free Add-On)\']') : `Item #${id}`
          }));
          this.isShowFreeAddOnDialog = true;
          this.hasShownFreeAddOnDialog = true;
          this.checkoutLoading = false;
          return;
        }
      }
      // Family Bundle - Dialog logic disabled (UI hidden)
      // if(this.isShowFamilyDiscountConfirmationDialog) {
      //   this.isShowFamilyDiscountConfirmationDialog = false
      //   return
      // }
      this.checkoutLoading = true;
      if (!this.patient?.birthdate) {
        this.showDobDialog = true;
        this.checkoutLoading = false;
        return;
      }
      if (!this.isAbleToCheckout()) {
        this.checkoutLoading = false;
        return;
      }
      if (orderType === "edit") {
        this.handleModifyOrder(orderType, this.editOrderPriceDiff, this.editOrderSampleId);
      } else if (orderType === "add") {
        this.handleModifyOrder(orderType, this.addOnOrderPriceDiff, this.isAddOnOrder);
      } else {
        // VP-15056 Total Immunoglobulins (167): add directly without showing dialog (only when not DBS)
        if (!this.isDbsVersion && eligibleAddons.includes(167)) {
          this.$store.dispatch('orderTest/addSelectedTest', { id: 167 });
        }
        // Family Bundle - Checkout logic disabled (UI hidden)
        // if (this.familyDiscountOrders.length > 0 && this.familyDiscountEligible) {
        //   this.checkoutLoading = false;
        //   this.isShowFamilyDiscountConfirmationDialog = true;
        //   return;
        // }
        // Route to appropriate pediatric flow based on beta flag
        if (this.isBeta) {
          this.handlePediatricFlowV2();
        } else {
          this.handlePediatricFlowV1();
        }
      }
    },

    // V2 Version - New complex pediatric flow for beta users
    handlePediatricFlowV2() {
      if (this.isMinorEffective) {
        let consent = readConsent();
        const skipProvider = this.skipPediatricConsentEffective; 

        if (consent && consent.consent_required === false) {
          this.showProviderConsent = true;
          this.showGuardianConsent = false;
          this.checkoutLoading = false;
          return;
        }

        if (skipProvider) {
          this.showProviderConsent = false;
          consent = writeConsent({ ...(consent || {}), consent_required: true, skip_pediatric_consent: true });
          const autoEmail = this.alwaysEmailConsentEffective;
          // Only auto-apply email consent defaults if there is no existing
          // consent_method or guardian info present (dialog should take precedence)
          const hasGuardianInfo = Boolean(consent?.consent_method || consent?.guardian_email || consent?.guardian_name || consent?.always_use_email_consent);
          if (autoEmail && !hasGuardianInfo) {
            const email = this.getGuardianEmailCandidate();
            if (email) {
              consent = writeConsent({
                ...consent,
                consent_method: 'email',
                guardian_email: email.toLowerCase(),
                always_use_email_consent: true
              });
              this.showGuardianConsent = false;
              this.checkoutLoading = true;
              this.handleNormalOrder();
              return;
            }
          }

          if (needGuardian(consent)) {
            this.guardianDialogDefaultAlways = Boolean(autoEmail);
            this.showGuardianConsent = true;
            this.checkoutLoading = false;
            return;
          }

          this.showGuardianConsent = false;
          this.handleNormalOrder();
          return;
        }

        this.ensureSkipProvider(false);
        if (needProvider(consent)) {
          this.showProviderConsent = true;
          this.showGuardianConsent = false;
          this.checkoutLoading = false;
          return;
        }
        if (needGuardian(consent)) {
          const autoEmail = this.alwaysEmailConsentEffective;
          if (autoEmail) {
            const email = this.getGuardianEmailCandidate();
            if (email) {
              const updatedConsent = writeConsent({
                ...consent,
                consent_method: 'email',
                guardian_email: email.toLowerCase(),
                always_use_email_consent: true
              });
              this.showGuardianConsent = false;
              this.showProviderConsent = false;
              this.handleNormalOrder();
              return;
            }
          }
          this.guardianDialogDefaultAlways = Boolean(autoEmail);
          this.showGuardianConsent = true;
          this.showProviderConsent = false;
          this.checkoutLoading = false;
          return;
        }
        this.showProviderConsent = false;
        this.showGuardianConsent = false;
      }
      this.handleNormalOrder();
    },

    // V1 Version - Old simple pediatric flow for production users
    handlePediatricFlowV1() {
      if (this.isUnder18(this.patient.birthdate)) {
        this.showProviderConsent = true;
        this.checkoutLoading = false;
        return;
      }
      this.handleNormalOrder();
    },

    handleNormalOrder() {
      this.setSessionStorage();
      const consentStr = sessionStorage.getItem('pediatricConsentInfo');
      this.pediatricConsentInfo = consentStr ? JSON.parse(consentStr) : null;
      if (this.pediatricConsentInfo) {
        sessionStorage.setItem('pediatricConsentInfo', JSON.stringify(this.pediatricConsentInfo));
      } else {
        sessionStorage.removeItem('pediatricConsentInfo');
      }
      this.$router.push("/ordertest/checkout");
      this.checkoutLoading = false;
    },

    /**
     * handle edit or add on orders
     * @param { string } orderType - type of order. values: edit, add
     * @param { number } chargeAmount - amount that need to pay/refund for the modified order
     * @param { string/number } sampleId - sampleId for the modified order. 
     */
    async handleModifyOrder(orderType, chargeAmount, sampleId) {
      if (chargeAmount !== 0 && !["wellProz", "netPay"].includes(this.paymentMethod)) {
        this.setSessionStorage();
        if (orderType === "edit") {
          sessionStorage.setItem("editOrderPriceDiff", chargeAmount);
        }
        if (orderType === "add") {
          sessionStorage.setItem("addOnOrderPriceDiff", chargeAmount);
        }
        this.checkoutLoading = false;
        this.$router.push("/ordertest/checkout");
      } else {
        try {
          let phone = (this.patient.areaCode + this.contactInfo.phone).trim().replace(/\D/g, '');
          let { use_credit, credit_type, credit_apply_amount, providerHasRevenue } = this.orderDraft;
          let order = {
            phone,
            orderItems: this.orderItemListForApi,
            priceDetails: this.firstOrderPriceDetails,
            deliveryMethod: this.deliveryMethod,
            paymentId: this.orderDraft.payment_id,
            patientId: this.patient.id,
            isUseCredit: use_credit ? use_credit : false,
            creditType: credit_type ? credit_type : null,
            usedCredit: credit_apply_amount ? Number(credit_apply_amount) : null,
            chargeMethod: this.paymentMethod,
            email: this.contactInfo.email,
            sendNotification: this.sendNotification,
            sendDate: this.sendDateInUtc,
            noteToPatient: this.orderDraft.note_to_patient,
            dbs: this.isDbsVersion,
            coupon: this.orderDraft.coupon,
            shippingFeeDetails: this.shippingFee,
            isUseConciergeFee: this.conciergeFee.isUse,
            isUseConsultationFee: this.consultationFee.isUse,
            currency: this.currency.name,
            providerHasRevenue
          };
          let editOrderForApi = getOrderObjForBackend(order, orderType, sampleId);
          let { data } = await editOrderApi(editOrderForApi);
          // save patient id to vuex
          this.$store.state.orderTest.user.patientId = this.patient.id  
          // redirect to order confirmation page for normal orders
          this.$router.push({ name: "orderConfirmation", params: { orderId: data.orderId } });
          this.checkoutLoading = false;
        } catch {
          this.checkoutLoading = false;
        } 
      }
    },

    setSessionStorage() {
      sessionStorage.setItem("deliveryMethod", JSON.stringify(this.deliveryMethod));
      sessionStorage.setItem("paymentMethod", this.paymentMethod);
      sessionStorage.setItem("sendEmail", JSON.stringify(this.sendNotification.email));
      sessionStorage.setItem("sendText", JSON.stringify(this.sendNotification.text));
      sessionStorage.setItem("sendDate", JSON.stringify(this.sendDate));
      sessionStorage.setItem("email", this.contactInfo.email);
      sessionStorage.setItem("phone", this.contactInfo.phone);
    },
    getCheckoutBlockReasonForEhr() {
      let { isError, message } = this.checkProcessToCheckout();
      if (isError) {
        return message;
      }

      if (!this.patient?.birthdate) {
        return "Patient date of birth is required before checkout.";
      }

      if (!this.isCovidOrderable()) {
        return this.covidWarningMessage || "Order cannot proceed due to Covid ordering restrictions.";
      }

      const isPPN = this.paymentMethod === "patientPayNow";

      if ((isPPN || this.isSelectedTaxableTest.length > 0) && this.isPatientAddressMissing) {
        return "Patient address is required before checkout.";
      }
      // VP-17999: DOB/Sex are mandatory for every practice — the express
      // checkout setting no longer exempts this gate.
      if (this.isPatientAgeGenderMissing) {
        return "Patient date of birth and gender are required before checkout.";
      }
      // Note: phone number check is skipped for EHR flow because
      // notification contact info is collected on the checkout page (Step 2)

      return "";
    },
    // check whether user is able to checkout
    isAbleToCheckout() {
      let { isError, message } = this.checkProcessToCheckout();
      if (isError) {
        notify({ type: 'error', message });
        return false;
      }
      // show warning dialog if covid is not orderable
      if (!this.isCovidOrderable()) {
        this.isShowCovidWarningDialog = true;
        return false;
      }

      const isPPN = this.paymentMethod === "patientPayNow"

      // VP-17999: DOB/Sex are mandatory for every practice — the express
      // checkout setting no longer exempts this gate.
      if((isPPN && this.isPatientAddressMissing) ||
         (this.isSelectedTaxableTest.length > 0 && this.isPatientAddressMissing) ||
         this.isPatientAgeGenderMissing ||
         this.isPatientPhoneMissing ) {
        this.expandPanelAndScrollToTop()
        return false
      } 
      return true
    },

    expandPanelAndScrollToTop() {
      // // check whether patient address info is completed
      // // if not, back to top and expand new order panel
      let Infocontainer = document.getElementById("update-patient-info-container")
      if (!Infocontainer) {
        return 
      }
      let emptyAddressInputElements = Infocontainer.getElementsByClassName("error");

      if (emptyAddressInputElements.length > 0) {
        // left part back to top
        document.getElementsByClassName("leftSide")[0].scrollTo(0, 0);
        // expand new order panel
        this.$store.dispatch("orderTest/setIsClickCheckout", true);
      }
    },

    checkProcessToCheckout() {
      let isError = false;
      let message = this.$t("OrderPage[\"Proceed\"]");
      if (this.containDryIce && this.deliveryMethod.dryIce === "shipToPatient") {
        let patientCountry = JSON.parse(sessionStorage.getItem("patient")).country;
        if (patientCountry !== "US") {
          message = this.$t("OrderPage[\"Dry ice kit can only be shipped within US.\"]");
          return { isError: true, message };
        }
      }
      if (!this.paymentMethod) {
        message = this.$t("OrderPage[\"Please choose a payment method.\"]");
        return { isError: true, message };
      }
      if (!this.sendNotification.email 
        && !this.sendNotification.text 
        && this.paymentMethod === "patientPayLater") 
      {
        message = this.$t("OrderPage[\"Missing notification method for Patient Pay Later, please choose send email and/or text message to notify patient\"]");
        return { isError: true, message };
      } 
      // error ElNotification for invalid date (chosen date is earlier than today)
      // if time is given, compare both date and time with now
      let date = new Date(`${this.sendDate.date} ${this.sendDate.time}`);
      if (moment(date).isBefore(moment(), "minute")) {
        message = this.$t("OrderPage[\"Please choose a future send date and time.\"]")
        return { isError: true, message };
      }
      // error message for invalid plan&save order date
      let elements = document.getElementsByClassName("planned-order-date-picker error");
      if (elements.length > 0) {
        message = this.$t("OrderPage[\"At least one plan & save order's date is invalid. Please choose a valid date.\"]")
        return { isError: true, message };
      }
      if (_.isEmpty(this.patient)) {
        message = this.$t("OrderPage[\"Please choose or create a patient to order.\"]")
        // highlight search patient input
        let searchUserInputElement = document.getElementsByClassName("user-container")[0]
          ?.getElementsByClassName("search-patient-input")?.[0];
        if (searchUserInputElement) {
          searchUserInputElement.classList.add("error");
            // left part back to top
            document.getElementsByClassName("leftSide")[0].scrollTo(0, 0);
        } else {
          // check whether add new patient form is open
          // if open, user not finish creating patient yet, show error message and scroll to create patient to ask user to finish creating patient
          let addNewPatientForm = document.getElementsByClassName("create-patient-form");
          if (addNewPatientForm.length > 0) {
            message = "Patient creation is not complete. To place an order, please click 'Create Patient' button first."
            // scrolling to create patient button
            let createPatientButton = Array.from(document.querySelectorAll('button'))?.find(button => button.textContent?.trim().toLowerCase() === "create patient");
            createPatientButton?.scrollIntoView({ behavior: "smooth", block: "center" });
          }
        }
        return { isError: true, message };
      }
      if(this.isTaxAPIError) {
        message = this.$t("OrderPage[\"Please choose a valid address for calculating tax\"]");
        return { isError: true, message };
      }
      /**
       * VP-10798 error message for orders containing sample type stool for NY patient
       * if delivery method is ship to patient
       * ************************************
       * 06/27/2025 VP-11532 update: not check delivery method
       *  if NY patient, block order containing stool, regardless of the shipment method selected.
       */
      let { shipToPatient, suppliedByClient } = this.orderedSampleTypes;
      let containStool = shipToPatient.concat(suppliedByClient).find(type => type?.toLowerCase() === "stool");
      let isNYPatient = getStateAbbreviation(this.patient.state || "").toUpperCase() === "NY";
      if (containStool && isNYPatient) {
        message = this.$t("OrderPage[\"Gut Zoomer is currently unavailable in New York State. Coming soon!\"]");
        return { isError: true, message };
      }
      
      /**
       * VP-14690: Block checkout for non-US patients if order contains Genetics category tests
       * VP-15061: Allow checkout for patients in user's country if user has genetics_whitelist beta tag
       */
      let hasGeneticsWhitelist = this.userInfo?.beta_programs && Array.isArray(this.userInfo.beta_programs) && this.userInfo.beta_programs.includes("genetics_whitelist");
      let patientCountry = this.patient?.country;
      let isNonUSPatient = patientCountry && patientCountry.toUpperCase() !== "US";
      let userCountry = this.userInfo?.customer_country ? String(this.userInfo.customer_country).toUpperCase() : "";
      let patientInUserCountry = patientCountry && userCountry && patientCountry.toUpperCase() === userCountry;
      if (isNonUSPatient) {
        // Get Genetics test IDs from vwCategories
        let geneticsCategory = this.vwCategories.find(
          category => category.categoryName && category.categoryName.toLowerCase() === 'genetics'
        );
        if (geneticsCategory && geneticsCategory.contents) {
          let geneticsTestIds = geneticsCategory.contents;
          // Check if any selected vw tests (with isSelect === true) are in Genetics category
          let selectedVwTests = this.vwTestList.filter(test => test.isSelect);
          let hasGeneticsTests = selectedVwTests.some(test => geneticsTestIds.includes(test.id));
          if (hasGeneticsTests) {
            // With genetics_whitelist, allow when patient is in user's country (US case already excluded by isNonUSPatient)
            let allowGeneticsForWhitelist = hasGeneticsWhitelist && patientInUserCountry;
            if (!allowGeneticsForWhitelist) {
              message = "Genetics tests are not available for non-US patients.";
              return { isError: true, message };
            }
          }
        }
      }
      
      return { isError, message };
    },

    getLabProcessingFee(orderItems) {
      // get lab processing fee
      let { orderTestsPrice } = this.priceDetails;
      let promoDiscount = this.$store.state.orderTest.order.promoDiscount || 0;
      let price = orderTestsPrice < promoDiscount ? 0 : orderTestsPrice - promoDiscount;
      const orderItemsWithouMerch = orderItems.filter(item => item.item_type != "merchandise");
      getLabProcessingFeeApi({ 
        orderItems: orderItemsWithouMerch, 
        original_price: price.toFixed(2), 
        patientId: this.patient ? Number(this.patient.id) : 0,
        useCoupon: promoDiscount > 0,
        ignorePatient: this.patient ? false : true,
        sampleId: this.editOrderSampleId
      }).then(res => {
        this.$store.dispatch("orderTest/order/setLabProcessingFee", res.data);
        this.plannedOrderList.forEach((order, index) => {
          let updatedPlannedOrder = { ...order };
        updatedPlannedOrder.labProcessingFee = res.data;
        this.$store.dispatch("orderTest/order/updatePlannedOrder", { index, updatedPlannedOrder });
        });
      });
    },

    saveDraft(isUpdateDraft) {
      let draftMessage = this.$t("OrderPage[\"Successfully save order draft.\"]");
      let objForConvertDraftFunction = {
        patientId: this.patient.id,
        paymentMethod: this.paymentMethod,
        labProcessingFee: this.priceDetails.labProcessingFee,
        contactInfo: this.contactInfo,
        sendNotification: this.sendNotification,
        orderItems: this.orderItemListForApi,
        draftName: this.draftName,
        deliveryMethod: this.deliveryMethod,
        sendDate: this.sendDateInUtc,
        isDbsVersion: this.isDbsVersion,
        plannedOrderList: this.plannedOrderList.map((item) => {
          return {sendDate: item.sendDate};
        }),
      }
      let draftForApi = getDraftObjFroApi(objForConvertDraftFunction);
      createOrderDraft(draftForApi).then(response => {
        if (isUpdateDraft && this.orderDraft.orderDraftId) {
          deleteDraftById(this.orderDraft.orderDraftId);
          draftMessage = this.$t("OrderPage[\"Successfully update order draft.\"]")
        }
        let newDraftId = response.data;
        console.log(newDraftId)
        notify({ type: 'success', title: this.$t("OrderPage[\"Success!\"]"), message: draftMessage });

        if (isUpdateDraft) {
          // console.log(newDraftId);
          this.$router.replace({ name: "Draft", params: { draftId: newDraftId } });
        } else {
          this.draftName = "";
        }
      });
    },

    // check whether any of delivery methods select shipToPatient option
    isNeedCompleteAddressInfo() {
      if (this.paymentMethod === "patientPayNow")
        return true;
      let isNeed = ["blood", "nonBlood", "dryIce"].some(prop => {
        let computeProp = "contain" + prop.charAt(0).toUpperCase() + prop.slice(1);
        return this.deliveryMethod[prop] === "shipToPatient" && this[computeProp];
      });
      return isNeed;
    },

    isCovidOrderable() {
      // use for testing, trigger order failure //
      let testing = localStorage.getItem("cypressTest");
      if (testing) {
        return true;
      }      
      // use for teting end //
      let isCovidOrdered = this.selectedTestIdList
        .some(testId => [198, 199, 201, 202].includes(testId));
      if (isCovidOrdered) {
        // return false if covid ordered with other tests
        if (this.selectedItemList.length > 1) {
          this.covidWarningMessage = this.$t("OrderPage[\"Covid 19 Fingerprick, Covid 19 Antibody, SARS-COVID 19 (Swab), Covid-19 +Flu A + Flu B Swab PCR must be ordered independently.\"]");
          return false;
        }
        // return false if covid ordered with patientPayLater
        if (this.paymentMethod === "patientPayLater") {
          this.covidWarningMessage = this.$t("OrderPage[\"Covid tests cannot be paid by “Patient Pay Later” payment method.\"]")
          return false;
        }
      }
      return true;
    },
    disableCheckout(bool) {
      if (bool || this.isUpdatingPatient) {
        this.isDisableCheckout = true;
      } else {
        this.isDisableCheckout = false;
      }
    },
    hideSuggestion(suggestion) {
      removeItem(suggestion, this.inAppPromos);
      localStorage.setItem("isHideSuggestionPromos", true);
    },
    // Family Bundle - Method disabled (UI hidden)
    // familyDiscountDialogConfirm() {
    //   this.isShowFamilyDiscountConfirmationDialog = false;
    //   this.handleNormalOrder();
    // },
    async onDobSubmittedFromDialog(dob) {
      if (!this.patient?.id) return;
      try {
        this.patient = { ...this.patient, birthdate: dob };
        sessionStorage.setItem("patient", JSON.stringify(this.patient));
        this.$store.dispatch("orderTest/order/setPatient", this.patient);
        this.$store.dispatch("orderTest/setIsPatientInfoInSessionStorage");

        const [mm, dd, yyyy] = dob.split("/");
        await updatePatientApi({
          patient_id: this.patient.id,
          patient_birthdate: `${yyyy}-${mm}-${dd}`,
        });

        this.$store.dispatch("orderTest/user/setUpdatePatientCount");
        notify({ type: 'success', title: 'Success!', message: 'Date of Birth updated.' });
        this.handleClickCheckout(this.orderType);
      } catch (e) {
        notify({ type: 'error', title: 'Error', message: 'Update Date of Birth failed.' });
      }
    },
    // V1 Version - Old simple DOB handling
    async onDobSubmittedFromDialogV1(dob) {
      if (!this.patient?.id) return;
      try {
        this.patient = { ...this.patient, birthdate: dob };
        sessionStorage.setItem("patient", JSON.stringify(this.patient));
        this.$store.dispatch("orderTest/order/setPatient", this.patient);
        this.$store.dispatch("orderTest/setIsPatientInfoInSessionStorage");

        const [mm, dd, yyyy] = dob.split("/");
        await updatePatientApi({
          patient_id: this.patient.id,
          patient_birthdate: `${yyyy}-${mm}-${dd}`,
        });

        this.$store.dispatch("orderTest/user/setUpdatePatientCount");
        notify({ type: 'success', title: 'Success!', message: 'Date of Birth updated.' });
        
        // Use V1 flow instead of full checkout
        this.showDobDialog = false;
        this.handlePediatricFlowV1();
      } catch (e) {
        notify({ type: 'error', title: 'Error', message: 'Update Date of Birth failed.' });
      }
    },
    isUnder18(dobStr) {
      if (!dobStr) return false;
      const dob = moment(dobStr, ['MM/DD/YYYY','YYYY-MM-DD'], true);
      if (!dob.isValid()) return false;
      const eighteenth = dob.clone().add(18, 'years');
      return moment().isBefore(eighteenth, 'day');
    },
    continueAfterConsent({ accepted, skipPediatricConsent }) {
      if (!accepted) { 
        this.checkoutLoading = false; 
        return; 
      }
      const next = writeConsent({
        consent_required: true,
        skip_pediatric_consent: Boolean(skipPediatricConsent),
      });
      if (needGuardian(next)) {
        this.showProviderConsent = false;
        this.showGuardianConsent = true;
        this.checkoutLoading = false;
      } else {
        this.showProviderConsent = false;
        this.checkoutLoading = true;
        this.handleNormalOrder();
      }
    },
    // V1 Version - Old simple consent handling
    continueAfterConsentV1(accepted) {
      if (!accepted) {
        this.checkoutLoading = false
        return
      }

      this.showProviderConsent = false
      this.checkoutLoading = true
      this.handleNormalOrder()
    },
    continueAfterGuardianConsent(info) {
      this.showGuardianConsent = false;
      const next = writeConsent(info);
      this.pediatricConsentInfo = next;
      this.checkoutLoading = true;
      this.handleNormalOrder();
    },
    getAddonRule(itemId) {
      const addRules = this.orderTestSupportObj?.exceptionMap?.ADDITION_ENABLE || [];
      return addRules.find(r => r.itemId === itemId);
    },
    collectEligibleAddons(ids) {
      const selectedIds = this.getSelectedIdsFromTree();
      const eligible = [];
      ids.forEach(id => {
        const rule = this.getAddonRule(id);
        const hit = rule && (rule.affectedTestIdList || [])
          .some(tid => selectedIds.has(Number(tid)));
        const alreadyHas = selectedIds.has(Number(id));
        if (hit && !alreadyHas) {
          eligible.push(id);
        }
      });
      return eligible;
    },
    getSelectedIdsFromTree() {
      return new Set(
        (this.orderTestSupportObj?.testNodeList || [])
          .filter(n => n?.value?.isSelect)
          .map(n => Number(n.id))
      );
    },
    onFreeAddOnDialogClose() {
      this.isShowFreeAddOnDialog = false;
      if (!this.isUrlFromEditOrAddOn) {
        this.handleClickCheckout(this.orderType);
      }
    },
    onDobUnder18CheckSubmit(selection) {
      const sel = String(selection).trim().toLowerCase();
      this.minorOverride = (sel === 'yes');
      this.showDobUnder18CheckDialog = false;

      if (this.minorOverride) {
        if (this.skipPediatricConsentEffective) {
          this.ensureSkipProvider(true); 
          this.showProviderConsent = false;
          this.showGuardianConsent = true;
        } else {
          this.ensureSkipProvider(false);
          this.showProviderConsent = true;
          this.showGuardianConsent = false;
        }
      } else {
        sessionStorage.removeItem('pediatricConsentInfo');
        this.pediatricConsentInfo = null;   
        this.showProviderConsent = false;        
        this.showGuardianConsent = false;
        this.handleNormalOrder();
      }
    },
    onUnknownDobClick() {
      this.showDobDialog = false;
      this.showDobUnder18CheckDialog = true;
    },
  },
};
</script>

<style scoped>
.order-summary-container {
  min-width: auto;
  width: auto;
  background-color: #f9fbfd;
  box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.5);
  scrollbar-width: thin;
  scrollbar-color: #c8d2de transparent;
}

.order-summary-container::-webkit-scrollbar {
  width: 4px;
  background: transparent;
}

.order-summary-container::-webkit-scrollbar-track {
  background: transparent;
}

.order-summary-container::-webkit-scrollbar-thumb {
  background: #c8d2de;
  border-radius: 999px;
}

.order-summary-container::-webkit-scrollbar-thumb:hover {
  background: #aebcca;
}

/* Sidebar styles matching NewOrderCartItems.vue */
.sidebar-top-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px dashed #c4c4c4;
  padding-bottom: 8px;
}

.sidebar-title-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.sidebar-expand-btn {
  width: 28px;
  height: 32px;
  border: none;
  border-radius: 4px;
  background: #004879;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
}

.sidebar-expand-icon {
  color: #fff;
  font-size: 16px;
  line-height: 1;
  font-weight: 700;
}

.sidebar-subtotal-label {
  color: #004879;
  font-size: 20px;
  font-weight: 500;
  line-height: 28px;
}

.sidebar-subtotal-value {
  color: #004879;
  font-size: 24px;
  font-weight: 400;
  line-height: 36px;
}

.sidebar-tabs-lib {
  margin-top: 8px;
}

.sidebar-tabs-lib :deep(.va-tab-group__main) {
  width: 100%;
}

.sidebar-tabs-lib :deep(.va-tab) {
  flex: 1;
  justify-content: center;
}

.sidebar-items {
  margin-top: 10px;
  border-bottom: 1px dashed #c4c4c4;
  padding-bottom: 4px;
  overflow-y: auto;
  overflow-x: hidden;
  scrollbar-width: thin;
  scrollbar-color: #c8d2de transparent;
}

.sidebar-items::-webkit-scrollbar {
  width: 4px;
  background: transparent;
}

.sidebar-items::-webkit-scrollbar-track {
  background: transparent;
}

.sidebar-items::-webkit-scrollbar-thumb {
  background: #c8d2de;
  border-radius: 999px;
}

.divide-line {
  border: 1px solid #e6e6e6;
}

/* toast transition settings starts */
/* enter transitions */
.toast-enter-from {
  opacity: 0;
  transform: translateY(-8px);
}
.toast-enter-to {
  opacity: 1;
  transform: translateY(0);
}
.toast-enter-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}
/* leave transitions */
/* .toast-leave-from {
  opacity: 1;
  transform: translateY(0);
} */
.toast-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}
.toast-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}
/* toast transition settings ends */

.highlight-text {
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
  letter-spacing: 0.4px;
}
</style>